import { useEffect, useState } from "react";
import { ChevronDown, AlertCircle, CheckCircle2 } from "lucide-react";
import merchantApi from "../../../lib/merchantApi";

/**
 * "Why is this incomplete?" diagnostic for an onboarding step.
 *
 * Hits GET /api/merchant/application/diagnose and surfaces, per required
 * field, whether it's present + valid + (if invalid) the reason. Merchants
 * stop staring at silent `partial` badges with no idea what's wrong.
 *
 * Triggered by step status. Only renders for status !== "complete" so a
 * happy step stays uncluttered. Auto-refetches when `status` changes (e.g.
 * after autosave reports a new status) and when the user expands it.
 */
export default function StepDiagnostic({ stepNum, status }) {
  const [open, setOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);

  // Refetch on (a) initial render at non-complete status, (b) status change.
  useEffect(() => {
    if (status === "complete") {
      setDiag(null);
      return;
    }
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await merchantApi.get("/merchant/application/diagnose");
        if (cancel) return;
        const step = (data?.steps || []).find((s) => s.step === stepNum);
        setDiag(step || null);
      } catch {
        // Silent fail — the diagnostic is a help feature, not a hard
        // dependency. The step still renders without it.
        if (!cancel) setDiag(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [stepNum, status]);

  if (status === "complete") return null;

  const invalid = (diag?.fields || []).filter((f) => !f.valid);
  if (invalid.length === 0 && !loading) return null;

  return (
    <div
      data-testid={`step-diagnostic-${stepNum}`}
      className="mt-6 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] overflow-hidden"
    >
      <button
        type="button"
        data-testid={`step-diagnostic-${stepNum}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-amber-400/[0.06] transition-colors text-left"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <AlertCircle className="h-3.5 w-3.5 text-amber-300 shrink-0" />
          <span className="text-[12.5px] text-amber-200">
            <span className="font-medium">Why is this step {status}?</span>{" "}
            <span className="text-amber-300/70">
              {invalid.length} field{invalid.length === 1 ? "" : "s"} need
              {invalid.length === 1 ? "s" : ""} attention
            </span>
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 text-amber-300 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 py-3 border-t border-amber-400/10">
          <ul className="space-y-2.5">
            {invalid.map((f) => (
              <li
                key={f.key}
                data-testid={`step-diagnostic-${stepNum}-field-${f.key}`}
                className="flex items-start gap-2.5"
              >
                <span className="h-1 w-1 rounded-full bg-amber-300 mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-amber-100">{f.label}</div>
                  <div className="text-[11px] text-amber-200/60 mt-0.5">
                    {f.reason || "needs a value"}
                    {f.saved_value !== null && f.saved_value !== undefined && (
                      <>
                        {" · "}
                        <span className="text-amber-200/40">saved as: </span>
                        <code className="text-amber-200/70 font-mono text-[10.5px]">
                          {typeof f.saved_value === "string"
                            ? `"${f.saved_value}"`
                            : JSON.stringify(f.saved_value)}
                        </code>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
            {diag?.fields?.filter((f) => f.valid).length > 0 && (
              <li className="flex items-center gap-2 pt-2 border-t border-amber-400/10 mt-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-300/70 shrink-0" />
                <span className="text-[10.5px] text-amber-200/40">
                  {diag.fields.filter((f) => f.valid).length} field
                  {diag.fields.filter((f) => f.valid).length === 1 ? "" : "s"} look good
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
